"use client";

import {
  Badge,
  Button,
  Icon,
  ModalContent,
  ModalFooter,
  ModalHeader,
  PortalModal,
  usePortalContext,
} from "@cloai/uikit";
import { ClothFilterBlock, ClothFilterSet } from "@cloai/uikit";
import { isEmpty } from "lodash";
import { useTranslations } from "next-intl";
import { FC, memo } from "react";

import { AiRequest } from "@/.gql/graphql";
import { useButtonIcon } from "@/components/user-flow/controls/useButtonIcon";
import { useClickUndress } from "@/components/user-flow/controls/useClickUndress";
import { useRenderItem } from "@/components/user-flow/undress-settings/useRenderItem";
import {
  type PhotoSettings,
  useProfileContext,
  useUndressContext,
  useUndressSettingsContext,
} from "@/providers";

import { STYLES } from "./styles";
import { PORTALS } from ".constants/portals";

export const CustomizeSettingsPortal: FC<{ token: string | null }> = memo(
  ({ token }) => {
    const t = useTranslations("UserFlowModal");
    const s = useTranslations("HomePage.settingsPopup");
    const b = useTranslations("HomePage");
    const profile = useProfileContext();
    const {
      clickSettings,
      clickUndressingFile,
      clickUndressStatus,
      initialSettings,
      reUseFaceObj,
      undressingCost,
      videoSettings,
    } = useUndressContext();
    const {
      ages,
      bodys,
      busts,
      butts,
      environments,
      hairColors,
      qualities,
      skinColors,
      styles,
    } = useUndressSettingsContext();
    const { closePortal } = usePortalContext();

    const { isLoading, isBlur } = clickUndressStatus;

    const disabled =
      (!clickUndressingFile && videoSettings.videoDuration === 0) ||
      isLoading ||
      isBlur;

    const { undressButtonIcon, reUseFaceDiscountTag } = useButtonIcon();

    const { renderFilterItems, noSelectedItem } = useRenderItem();

    const {
      age,
      bodyType,
      breastSize,
      buttSize,
      environment,
      hairColor,
      quality,
      set,
      skinColor,
      style,
    } = clickSettings;

    const { onSignUndressImage } = useClickUndress(token);

    /**
     * Рендерит секцию настроек.
     * @param index - Индекс секции
     * @param items - Данные для рендера
     * @param selectedItem - Текущее выбранное значение
     * @param itemType - Тип настройки (`costume`, `pose`, `set`)
     * @param extraItems - Дополнительные элементы (например, `noSelectedItem`)
     */
    const renderSection = (
      title: string,
      items: AiRequest[],
      selectedItem: string | number,
      itemType: keyof PhotoSettings,
      extraItems: AiRequest[] = [],
    ) =>
      !isEmpty(items) && (
        <ClothFilterBlock title={title}>
          <ClothFilterSet>
            {renderFilterItems(
              [...extraItems, ...items],
              selectedItem,
              itemType,
            )}
          </ClothFilterSet>
        </ClothFilterBlock>
      );

    return (
      <PortalModal
        portalId={PORTALS.USER_FLOW_CUSTOMIZE}
        style={STYLES.portal.variants.medium}
      >
        <ModalHeader>{t("titles.customizeBody")}</ModalHeader>

        <ModalContent className="@container">
          {set !== initialSettings.set && (
            <>
              {renderSection(
                s("hairColor"),
                hairColors,
                hairColor,
                "hairColor",
                [noSelectedItem(s("hairColorDefault"), 1)],
              )}
              {renderSection(
                s("skinColor"),
                skinColors,
                skinColor,
                "skinColor",
                [noSelectedItem(s("hairColorDefault"), 1)],
              )}
              {renderSection(
                s("environment"),
                environments,
                environment,
                "environment",
                [noSelectedItem(s("environmentDefault"), 1)],
              )}
            </>
          )}

          {renderSection(s("bustSize"), busts, breastSize, "breastSize")}
          {renderSection(s("buttSize"), butts, buttSize, "buttSize")}
          {renderSection(s("quality"), qualities, quality, "quality")}
          {renderSection(s("style"), styles, style, "style")}
          {renderSection(s("age"), ages, age, "age")}
          {renderSection(s("bodyType"), bodys, bodyType, "bodyType")}
        </ModalContent>

        <ModalFooter>
          <Badge
            content={reUseFaceDiscountTag}
            size="lg"
            color="secondary"
            wide
            isInvisible={!reUseFaceObj || !undressingCost}
            classNames={{ base: "mx-auto max-w-110" }}
          >
            <Button
              key="button-undress"
              startContent={<Icon icon="double_arrow" />}
              endContent={profile && undressButtonIcon}
              color="primary"
              wide
              onClick={() => {
                onSignUndressImage();
                closePortal();
              }}
              loading={isLoading}
              disabled={disabled}
            >
              {b("startGenerate")}
            </Button>
          </Badge>
        </ModalFooter>
      </PortalModal>
    );
  },
);

CustomizeSettingsPortal.displayName = "CustomizeSettingsPortal";
